import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  Link
} from '@mui/material';
import {
  CheckCircle,
  Error,
  PlayArrow,
  Description,
  Code,
  CloudUpload,
  Settings,
  Terminal,
  ExpandMore,
  Rocket
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0'
}));

const CodeBlock = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f5f5',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  overflowX: 'auto',
  border: '1px solid #e0e0e0',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1)
}));

const CommandBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#1e1e1e',
  color: '#d4d4d4',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
  fontSize: '0.875rem',
  overflowX: 'auto',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  position: 'relative'
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} id={`deployment-tabpanel-${index}`}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const DockerK8sSupport: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState(0);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);
  const handleReset = () => setActiveStep(0);

  const deploymentSteps = [
    {
      label: 'Prerequisites Check',
      description: 'Verify all required tools are installed',
      commands: [
        'docker --version',
        'docker-compose --version',
        'kubectl version --client',
        'minikube version'
      ]
    },
    {
      label: 'Start Minikube',
      description: 'Initialize Kubernetes cluster',
      commands: [
        'minikube start --driver=docker --memory=4096 --cpus=2',
        'minikube status'
      ]
    },
    {
      label: 'Configure Docker Environment',
      description: 'Point Docker CLI to Minikube\'s Docker daemon',
      commands: [
        'eval $(minikube docker-env)',
        'echo "Docker environment configured for Minikube"'
      ]
    },
    {
      label: 'Build Docker Image',
      description: 'Build the application image inside Minikube',
      commands: [
        'docker build -t e-unify-app:latest .',
        'docker images | grep e-unify-app'
      ]
    },
    {
      label: 'Create Kubernetes Secrets',
      description: 'Set up MongoDB connection and JWT secrets',
      commands: [
        'kubectl create secret generic e-unify-secrets \\',
        '  --from-literal=MONGO_URI=mongodb://e-unify-mongo:27017/e-unify \\',
        '  --from-literal=JWT_SECRET=your_jwt_secret_here \\',
        '  --from-literal=EMAIL_PASSWORD=your_sendgrid_key \\',
        '  --dry-run=client -o yaml | kubectl apply -f -'
      ]
    },
    {
      label: 'Deploy MongoDB',
      description: 'Deploy MongoDB with persistent storage',
      commands: [
        'kubectl apply -f k8s/mongo.yaml',
        'kubectl wait --for=condition=ready pod -l app=e-unify-mongo --timeout=120s'
      ]
    },
    {
      label: 'Deploy Application',
      description: 'Deploy the E-Unify application',
      commands: [
        'kubectl apply -f k8s/configmap.yaml',
        'kubectl apply -f k8s/deployment.yaml',
        'kubectl wait --for=condition=ready pod -l app=e-unify-app --timeout=120s'
      ]
    },
    {
      label: 'Verify Deployment',
      description: 'Check pod status and logs',
      commands: [
        'kubectl get pods',
        'kubectl get svc',
        'kubectl logs -l app=e-unify-app --tail=50'
      ]
    },
    {
      label: 'Access Application',
      description: 'Get the application URL',
      commands: [
        'minikube service e-unify-app-lb --url'
      ]
    }
  ];

  const troubleshootingItems = [
    {
      issue: 'ImagePullBackOff',
      solution: 'Ensure you ran `eval $(minikube docker-env)` before building the image. The image must be built inside Minikube\'s Docker environment.',
      commands: ['eval $(minikube docker-env)', 'docker build -t e-unify-app:latest .']
    },
    {
      issue: 'CrashLoopBackOff - MongoDB',
      solution: 'Check MongoDB health check commands are properly quoted in k8s/mongo.yaml',
      commands: ['kubectl logs -l app=e-unify-mongo', 'kubectl describe pod -l app=e-unify-mongo']
    },
    {
      issue: 'CrashLoopBackOff - App',
      solution: 'Verify MongoDB connection string and health check endpoint. App uses /api/v1/dashboard/health-check (unprotected)',
      commands: ['kubectl logs -l app=e-unify-app', 'kubectl get secret e-unify-secrets -o yaml']
    },
    {
      issue: 'Connection Refused',
      solution: 'Ensure MongoDB service name matches MONGO_URI. Should be mongodb://e-unify-mongo:27017/e-unify',
      commands: ['kubectl get svc', 'kubectl exec -it <app-pod> -- env | grep MONGO']
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          <Rocket sx={{ mr: 1, verticalAlign: 'middle' }} />
          Docker & Kubernetes Deployment Guide
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete guide for deploying E-Unify to Kubernetes with automated deployment scripts
        </Typography>
      </Box>

      {/* Quick Actions */}
      <StyledPaper elevation={2}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Quick Deploy
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Use the automated deployment script to deploy with a single command
        </Alert>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            sx={{ 
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' }
            }}
            href="#automated-deployment"
          >
            Automated Deployment
          </Button>
          <Button
            variant="outlined"
            startIcon={<Description />}
            sx={{ 
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': { borderColor: 'primary.dark', backgroundColor: 'rgba(0, 51, 102, 0.04)' }
            }}
            href="#manual-deployment"
          >
            Manual Steps
          </Button>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            sx={{ 
              borderColor: 'secondary.main',
              color: 'secondary.main',
              '&:hover': { borderColor: '#8B1515', backgroundColor: 'rgba(179, 27, 27, 0.04)' }
            }}
            href="#troubleshooting"
          >
            Troubleshooting
          </Button>
        </Box>
      </StyledPaper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="deployment tabs"
          sx={{
            '& .MuiTab-root': { color: 'primary.main' },
            '& .Mui-selected': { color: 'primary.main', fontWeight: 'bold' }
          }}
        >
          <Tab label="Automated Deployment" id="deployment-tab-0" />
          <Tab label="Manual Deployment" id="deployment-tab-1" />
          <Tab label="Troubleshooting" id="deployment-tab-2" />
          <Tab label="Testing" id="deployment-tab-3" />
        </Tabs>
      </Box>

      {/* Tab 0: Automated Deployment */}
      <TabPanel value={tabValue} index={0}>
        <StyledPaper id="automated-deployment">
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            <Terminal sx={{ mr: 1, verticalAlign: 'middle' }} />
            Automated Deployment Script
          </Typography>
          
          <Alert severity="success" sx={{ mb: 3 }}>
            The deployment script automates all manual steps into a single command
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            1. Download the Deployment Script
          </Typography>
          <CommandBox>
            <code>
              # Script is located at: scripts/deploy-k8s.sh<br />
              chmod +x scripts/deploy-k8s.sh
            </code>
          </CommandBox>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            2. Run the Deployment
          </Typography>
          <CommandBox>
            <code>
              # Full deployment<br />
              ./scripts/deploy-k8s.sh<br />
              <br />
              # Skip image rebuild (if image already exists)<br />
              ./scripts/deploy-k8s.sh --skip-build<br />
              <br />
              # Clean deployment (delete existing resources first)<br />
              ./scripts/deploy-k8s.sh --clean
            </code>
          </CommandBox>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            3. What the Script Does
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
              <ListItemText primary="Checks prerequisites (Docker, kubectl, minikube)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
              <ListItemText primary="Starts Minikube if not running" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
              <ListItemText primary="Configures Docker environment for Minikube" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
              <ListItemText primary="Builds Docker image inside Minikube" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
              <ListItemText primary="Creates Kubernetes secrets" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
              <ListItemText primary="Deploys MongoDB with health checks" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
              <ListItemText primary="Deploys application with proper configuration" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
              <ListItemText primary="Waits for pods to be ready" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle sx={{ color: '#4caf50' }} /></ListItemIcon>
              <ListItemText primary="Displays access URL" />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            4. Script Output
          </Typography>
          <CodeBlock>
            <code style={{ color: '#4caf50' }}>✓ Prerequisites check passed</code><br />
            <code style={{ color: '#4caf50' }}>✓ Minikube is running</code><br />
            <code style={{ color: '#4caf50' }}>✓ Docker environment configured</code><br />
            <code style={{ color: '#4caf50' }}>✓ Image built successfully</code><br />
            <code style={{ color: '#4caf50' }}>✓ Secrets created</code><br />
            <code style={{ color: '#4caf50' }}>✓ MongoDB deployed and ready</code><br />
            <code style={{ color: '#4caf50' }}>✓ Application deployed and ready</code><br />
            <code style={{ color: '#2196f3' }}>→ Access your application at: http://192.168.49.2:32307</code>
          </CodeBlock>
        </StyledPaper>
      </TabPanel>

      {/* Tab 1: Manual Deployment */}
      <TabPanel value={tabValue} index={1}>
        <StyledPaper id="manual-deployment">
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Manual Deployment Steps
          </Typography>
          
          <Stepper activeStep={activeStep} orientation="vertical">
            {deploymentSteps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography variant="h6">{step.label}</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {step.description}
                  </Typography>
                  <CommandBox>
                    {step.commands.map((cmd, i) => (
                      <code key={i}>
                        {cmd}
                        {i < step.commands.length - 1 && <br />}
                      </code>
                    ))}
                  </CommandBox>
                  <Box sx={{ mb: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ 
                        mt: 1, 
                        mr: 1,
                        backgroundColor: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.dark' }
                      }}
                    >
                      {index === deploymentSteps.length - 1 ? 'Finish' : 'Continue'}
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
          {activeStep === deploymentSteps.length && (
            <Paper square elevation={0} sx={{ p: 3 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="h6">Deployment Complete!</Typography>
                <Typography variant="body2">
                  Your E-Unify application is now running on Kubernetes
                </Typography>
              </Alert>
              <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                Reset Steps
              </Button>
            </Paper>
          )}
        </StyledPaper>
      </TabPanel>

      {/* Tab 2: Troubleshooting */}
      <TabPanel value={tabValue} index={2}>
        <StyledPaper id="troubleshooting">
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            <Error sx={{ mr: 1, verticalAlign: 'middle' }} />
            Troubleshooting Guide
          </Typography>

          {troubleshootingItems.map((item, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={item.issue} color="error" size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {item.solution}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Diagnostic Commands:
                </Typography>
                <CommandBox>
                  {item.commands.map((cmd, i) => (
                    <code key={i}>
                      {cmd}
                      {i < item.commands.length - 1 && <br />}
                    </code>
                  ))}
                </CommandBox>
              </AccordionDetails>
            </Accordion>
          ))}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Common Commands
          </Typography>
          <CommandBox>
            <code># View pod status</code><br />
            <code>kubectl get pods -w</code><br />
            <br />
            <code># View pod logs</code><br />
            <code>kubectl logs -l app=e-unify-app --tail=100</code><br />
            <br />
            <code># Describe pod (detailed info)</code><br />
            <code>kubectl describe pod &lt;pod-name&gt;</code><br />
            <br />
            <code># Restart deployment</code><br />
            <code>kubectl rollout restart deployment e-unify-app</code><br />
            <br />
            <code># Delete and recreate pods</code><br />
            <code>kubectl delete pod -l app=e-unify-app</code><br />
            <br />
            <code># Check secrets</code><br />
            <code>kubectl get secret e-unify-secrets -o yaml</code>
          </CommandBox>
        </StyledPaper>
      </TabPanel>

      {/* Tab 3: Testing */}
      <TabPanel value={tabValue} index={3}>
        <StyledPaper>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Testing Your Deployment
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            1. Health Check
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Test the application health endpoint:
          </Typography>
          <CommandBox>
            <code># Get the service URL</code><br />
            <code>export APP_URL=$(minikube service e-unify-app-lb --url)</code><br />
            <br />
            <code># Test health endpoint</code><br />
            <code>curl $APP_URL/api/v1/dashboard/health-check</code><br />
            <br />
            <code># Expected response:</code><br />
            <code style={{ color: '#4caf50' }}>
              {JSON.stringify({ status: 'healthy', timestamp: '2024-01-01T00:00:00.000Z' }, null, 2)}
            </code>
          </CommandBox>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            2. Database Connection
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Verify MongoDB connectivity:
          </Typography>
          <CommandBox>
            <code># Check MongoDB pod</code><br />
            <code>kubectl get pod -l app=e-unify-mongo</code><br />
            <br />
            <code># Test MongoDB connection from app pod</code><br />
            <code>kubectl exec -it $(kubectl get pod -l app=e-unify-app -o jsonpath='&#123;.items[0].metadata.name&#125;') -- sh -c "nc -zv e-unify-mongo 27017"</code>
          </CommandBox>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            3. Application Logs
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Check for successful startup messages:
          </Typography>
          <CommandBox>
            <code>kubectl logs -l app=e-unify-app --tail=50 | grep -E "(MongoDB Connected|Server is running)"</code>
          </CommandBox>
          <CodeBlock>
            <code style={{ color: '#4caf50' }}>✅ MongoDB Connected: e-unify-mongo</code><br />
            <code style={{ color: '#4caf50' }}>   Database: e-unify</code><br />
            <code style={{ color: '#4caf50' }}>Server is running on port 3002</code>
          </CodeBlock>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            4. Access the Application
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Open the application in your browser:
          </Typography>
          <CommandBox>
            <code># Get the URL and open in browser</code><br />
            <code>minikube service e-unify-app-lb</code>
          </CommandBox>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Expected Results:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} /></ListItemIcon>
                <ListItemText primary="All pods show 1/1 READY" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} /></ListItemIcon>
                <ListItemText primary="Health check returns 200 OK" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} /></ListItemIcon>
                <ListItemText primary="Logs show MongoDB connection" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} /></ListItemIcon>
                <ListItemText primary="Application loads in browser" />
              </ListItem>
            </List>
          </Alert>
        </StyledPaper>
      </TabPanel>

      {/* Additional Resources */}
      <StyledPaper>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Additional Resources
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon><Description /></ListItemIcon>
            <ListItemText 
              primary={<Link href="/k8s/README.md" target="_blank">Kubernetes README</Link>}
              secondary="Detailed deployment documentation"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><Code /></ListItemIcon>
            <ListItemText 
              primary={<Link href="https://kubernetes.io/docs/" target="_blank" rel="noopener">Kubernetes Documentation</Link>}
              secondary="Official Kubernetes documentation"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CloudUpload /></ListItemIcon>
            <ListItemText 
              primary={<Link href="https://minikube.sigs.k8s.io/docs/" target="_blank" rel="noopener">Minikube Documentation</Link>}
              secondary="Local Kubernetes development"
            />
          </ListItem>
        </List>
      </StyledPaper>
    </Container>
  );
};

export default DockerK8sSupport;
